import React, { useEffect, useMemo, useRef } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Typography,
  Chip,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import PeopleIcon from '@material-ui/icons/People';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Header, Page, Content } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { getRoles } from './types';
import { useOnboardingTranslation } from '../../translations';
import { LanguageSelector } from './LanguageSelector';
import { getOnboardingColors } from './colors';
import { createOnboardingApiClient } from '../../api/onboardingApi';

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
    roleQuestion: {
      fontWeight: 700,
      fontSize: '1.6rem',
      margin: theme.spacing(1, 0),
    },
    roleCardContainer: {
      justifyContent: 'center',
    },
    roleCard: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
      },
    },
    roleCardContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: theme.spacing(4, 3),
      flexGrow: 1,
    },
    roleIconWrapper: {
      color: c.iconDefault,
      marginBottom: theme.spacing(2),
    },
    roleIconColor: {
      color: c.primary,
    },
    roleName: {
      fontWeight: 700,
      fontSize: '1.4rem',
      marginBottom: theme.spacing(1),
    },
    roleDescription: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
      fontSize: '0.95rem',
    },
    badgeRow: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      justifyContent: 'center',
      marginBottom: theme.spacing(3),
    },
    badge: {
      borderRadius: 16,
      fontWeight: 600,
      fontSize: '0.75rem',
      backgroundColor: c.badgeBg,
      border: `1px solid ${c.badgeBorder}`,
      color: c.badgeText,
    },
    rolePoints: {
      textAlign: 'left',
      width: '100%',
      paddingLeft: theme.spacing(2),
      marginBottom: theme.spacing(3),
      flexGrow: 1,
      '& li': {
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.9rem',
      },
    },
    startButton: {
      textTransform: 'none',
      marginTop: 'auto',
      fontWeight: 700,
      fontSize: '0.85rem',
      letterSpacing: 1,
      padding: theme.spacing(1.2, 4),
      borderRadius: 4,
      backgroundColor: c.buttonPrimaryBg,
      color: c.buttonPrimaryFg,
      '&:hover': {
        backgroundColor: c.primaryHover,
      },
    },
    disabledCard: {
      opacity: 0.6,
      cursor: 'default',
      '&:hover': {
        transform: 'none',
        boxShadow: 'none',
      },
    },
    disabledButton: {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      '&:hover': {
        backgroundColor: theme.palette.action.disabledBackground,
      },
    },
    prerequisitesBox: {
      marginTop: theme.spacing(6),
      padding: theme.spacing(3, 4),
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      maxWidth: 720,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    prerequisitesHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    prerequisitesIcon: {
      color: c.primary,
      fontSize: '1.4rem',
    },
    prerequisitesTitle: {
      fontWeight: 700,
      fontSize: '1.05rem',
    },
    prerequisitesDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.9rem',
      marginBottom: theme.spacing(2),
    },
    prerequisitesList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      '& li': {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: '0.9rem',
        '&:last-child': {
          borderBottom: 'none',
        },
      },
    },
    prerequisitesActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(3),
      marginTop: theme.spacing(2),
    },
    prerequisiteActionLink: {
      color: c.buttonPrimaryFg,
      backgroundColor: c.buttonPrimaryBg,
      fontWeight: 600,
      fontSize: '0.85rem',
      textDecoration: 'none',
      padding: theme.spacing(0.8, 2.5),
      borderRadius: 4,
      '&:hover': {
        backgroundColor: c.primaryHover,
        textDecoration: 'none',
      },
    },
    prerequisiteHelpLink: {
      color: c.primary,
      fontWeight: 600,
      fontSize: '0.85rem',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    prerequisiteLink: {
      color: c.primary,
      fontWeight: 600,
      fontSize: '0.85rem',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    prerequisitesNote: {
      marginTop: theme.spacing(2),
      color: theme.palette.text.secondary,
      fontSize: '0.85rem',
      fontStyle: 'italic',
    },
  };
});

export const OnboardingPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useOnboardingTranslation();
  const roles = useMemo(() => getRoles(t), [t]);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const apiClient = useMemo(
    () => createOnboardingApiClient({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );

  // Try to load saved role from DB first, then fall back to localStorage
  const checkedDb = useRef(false);
  useEffect(() => {
    const savedRole = localStorage.getItem('onboarding_selected_role');
    if (savedRole && roles.some(r => r.id === savedRole)) {
      navigate(`/onboarding/${savedRole}`, { replace: true });
      return;
    }

    if (checkedDb.current) return;
    checkedDb.current = true;

    apiClient.getProgress().then(progress => {
      if (progress.role_id && roles.some(r => r.id === progress.role_id)) {
        localStorage.setItem('onboarding_selected_role', progress.role_id);
        if (progress.completed_journeys?.length) {
          localStorage.setItem('onboarding_completed_journeys', JSON.stringify(progress.completed_journeys));
        }
        navigate(`/onboarding/${progress.role_id}`, { replace: true });
      }
    }).catch(() => {
      // DB unavailable — stay on role selection
    });
  }, [navigate, roles, apiClient]);

  const handleRoleSelect = (roleId: string) => {
    const previousRole = localStorage.getItem('onboarding_selected_role');
    localStorage.setItem('onboarding_selected_role', roleId);
    if (previousRole !== roleId) {
      localStorage.removeItem('onboarding_completed_journeys');
    }

    // Also persist to the database
    apiClient.selectRole(roleId).catch(() => {
      // DB save failed silently — localStorage already updated
    });

    navigate(`/onboarding/${roleId}`);
  };

  return (
    <Page themeId="tool">
      <Header title={t('rolePageTitle')} subtitle={t('rolePageSubtitle')} />
      <Content>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <Typography className={classes.roleQuestion}>
            {t('roleQuestion')}
          </Typography>

          <LanguageSelector />
        </Box>

        <Grid container spacing={4} className={classes.roleCardContainer}>
          {roles.map(role => {
            const isDisabled = role.id === 'techlead';
            return (
              <Grid item xs={12} sm={6} md={5} key={role.id}>
                <Card
                  className={`${classes.roleCard} ${
                    isDisabled ? classes.disabledCard : ''
                  }`}
                  onClick={() => !isDisabled && handleRoleSelect(role.id)}
                >
                  <CardContent className={classes.roleCardContent}>
                    <Box className={classes.roleIconWrapper}>
                      {role.id === 'developer' ? (
                        <PersonIcon
                          style={{ fontSize: 64 }}
                          className={classes.roleIconColor}
                        />
                      ) : (
                        <PeopleIcon
                          style={{ fontSize: 64 }}
                          className={classes.roleIconColor}
                        />
                      )}
                    </Box>

                    <Typography className={classes.roleName}>
                      {role.title}
                    </Typography>
                    <Typography className={classes.roleDescription}>
                      {role.description}
                    </Typography>

                    <Box className={classes.badgeRow}>
                      <Chip
                        label={t('journeysCount', {
                          count: role.journeys.length,
                        })}
                        size="small"
                        variant="outlined"
                        className={classes.badge}
                      />
                      <Chip
                        label={role.duration}
                        size="small"
                        variant="outlined"
                        className={classes.badge}
                      />
                      <Chip
                        label={role.badgeExtra}
                        size="small"
                        variant="outlined"
                        className={classes.badge}
                      />
                      {role.id === 'developer' && (
                        <Chip
                          label={t('npsBadge')}
                          size="small"
                          variant="outlined"
                          className={classes.badge}
                        />
                      )}
                    </Box>

                    <ul className={classes.rolePoints}>
                      {role.points.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>

                    <Button
                      variant="contained"
                      className={`${classes.startButton} ${
                        isDisabled ? classes.disabledButton : ''
                      }`}
                      disabled={isDisabled}
                      onClick={e => {
                        e.stopPropagation();
                        handleRoleSelect(role.id);
                      }}
                    >
                      {isDisabled
                        ? t('comingSoon')
                        : t('startAs', { role: role.title })}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box className={classes.prerequisitesBox}>
          <Box className={classes.prerequisitesHeader}>
            <ErrorOutlineIcon className={classes.prerequisitesIcon} />
            <Typography className={classes.prerequisitesTitle}>
              {t('prerequisitesTitle')}
            </Typography>
          </Box>
          <Typography className={classes.prerequisitesDescription}>
            {t('prerequisitesDescription')}
          </Typography>
          <ul className={classes.prerequisitesList}>
            <li><span>{t('prerequisiteGithubLicense')}</span></li>
            <li><span>{t('prerequisiteGithubCopilot')}</span></li>
            <li><span>{t('prerequisiteGithubCodespaces')}</span></li>
          </ul>
          <Typography className={classes.prerequisitesNote}>
            {t('prerequisitesAlignWithTechLead')}
          </Typography>
          <Box className={classes.prerequisitesActions}>
            <Link
              href="https://nestle.service-now.com/itsp?id=nesa_itsp_sc_cat_item&sys_id=30c070f7c3dc9a18be94140ed40131ac"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.prerequisiteActionLink}
            >
              {t('prerequisitesRequestAccess')} →
            </Link>
            <Link
              href="https://nestle.atlassian.net/wiki/spaces/MSEAPD/pages/454017637/How+to+get+Microsoft+Copilot+license"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.prerequisiteHelpLink}
            >
              {t('prerequisitesHowToFill')}
            </Link>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
